import { AnimatePresence, motion } from 'framer-motion'
import {
  Clock,
  Filter,
  Hexagon,
  Home,
  Layers2,
  Map as MapIcon,
  Network,
  Save,
  X,
} from 'lucide-react'
import type { Map as MapLibreMap } from 'maplibre-gl'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SidebarOpenButton } from '@/components/investigations/SidebarOpenButton'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useGeofenceStore } from '@/stores/geofence-store'
import { type BaseLayer, useGeolocationStore } from '@/stores/geolocation-store'
import { cn } from '@/utils/cn'

const SUPPORTED_LAYERS: { id: BaseLayer; label: string }[] = [
  { id: 'dark-hc', label: 'Dark+' },
  { id: 'dark', label: 'Dark' },
  { id: 'osm', label: 'Light' },
  { id: 'streets', label: 'Streets' },
  { id: 'satellite', label: 'Satellite' },
  { id: 'hybrid', label: 'Hybrid' },
]

const HOME_VIEW = { center: [14.4378, 50.0755] as [number, number], zoom: 11 }

interface MapToolbarProps {
  mapRef: React.RefObject<MapLibreMap | null>
  // Right offset in px; animated when sidebar opens/closes.
  rightOffset: number
  sidebarOpen: boolean
  onOpenSidebar: () => void
  // Optional — when provided, the Filter button appears at the top of the
  // toolbar and toggles the parent-rendered filter panel. Omitted in views
  // that don't surface entity-type filtering.
  showFilterPanel?: boolean
  onToggleFilter?: () => void
  // Optional — when provided, the fan-out tools button is rendered. The
  // fan exposes the timeline scrubber (already wired via the store) and
  // the synced-graph support view, the latter controlled by these props.
  supportViewOpen?: boolean
  onToggleSupportView?: () => void
}

// Right-edge vertical toolbar. Animates `right` when the entity sidebar
// opens so the buttons clear the panel. Glass surface, theme-adaptive.
// When sidebar is closed, an extra primary-coloured "open sidebar" button
// is appended at the bottom of the stack.
export function MapToolbar({
  mapRef,
  rightOffset,
  sidebarOpen,
  onOpenSidebar,
  showFilterPanel,
  onToggleFilter,
  supportViewOpen,
  onToggleSupportView,
}: MapToolbarProps) {
  const { t } = useTranslation()
  return (
    <TooltipProvider delayDuration={250}>
      <motion.div
        animate={{ right: rightOffset }}
        transition={{ type: 'spring', stiffness: 380, damping: 32, mass: 0.9 }}
        className="pointer-events-auto absolute top-3 z-10 flex flex-col gap-1.5"
      >
        {onToggleFilter && (
          <FilterButton active={showFilterPanel ?? false} onClick={onToggleFilter} />
        )}
        <ToolsFanButton
          supportViewOpen={supportViewOpen ?? false}
          onToggleSupportView={onToggleSupportView}
        />
        <DrawButtons />
        <ResetButton mapRef={mapRef} />
        <BasemapButton />
        <AnimatePresence initial={false}>
          {!sidebarOpen && (
            <SidebarOpenButton
              key="open-sidebar"
              onClick={onOpenSidebar}
              label={t('geo.tools.openSidebar')}
              className="mt-1"
            />
          )}
        </AnimatePresence>
      </motion.div>
    </TooltipProvider>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Atomic tool button — single source of truth for state visuals.

interface ToolButtonProps {
  icon: React.ReactNode
  label?: string
  tooltip: string
  active?: boolean
  disabled?: boolean
  attached?: boolean // popover/menu open
  onClick?: () => void
  className?: string
}

const TOOL_BUTTON_CLASSES = (active?: boolean, attached?: boolean, disabled?: boolean) =>
  cn(
    'group relative inline-flex h-10 min-w-10 items-center justify-center gap-1.5 rounded-xl px-2',
    'border text-[11px] font-medium tracking-wide',
    'backdrop-blur-xl backdrop-saturate-150 transition-all duration-150',
    'shadow-[0_10px_28px_-12px_hsl(var(--foreground)/0.22),inset_0_1px_0_hsl(0_0%_100%/0.06)]',
    !active &&
      !attached &&
      'border-border/40 bg-card/72 text-foreground/85 hover:border-border/60 hover:bg-card/85 hover:text-foreground hover:scale-[1.02]',
    (active || attached) &&
      'border-primary bg-primary text-primary-foreground shadow-[0_10px_28px_-8px_hsl(var(--primary)/0.4),inset_0_1px_0_hsl(0_0%_100%/0.10)]',
    disabled && 'opacity-40 pointer-events-none'
  )

function ToolButton({
  icon,
  label,
  tooltip,
  active,
  disabled,
  attached,
  onClick,
  className,
}: ToolButtonProps) {
  return (
    <ToolTooltip label={tooltip}>
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className={cn(TOOL_BUTTON_CLASSES(active, attached, disabled), className)}
      >
        <span className="flex h-5 w-5 items-center justify-center">{icon}</span>
        {label && <span className="text-[10px] uppercase tracking-[0.14em]">{label}</span>}
        {attached && (
          <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-primary-foreground" />
        )}
      </button>
    </ToolTooltip>
  )
}

// Classic hover tooltip — solid dark bubble, small arrow, white text.
// Uses the default shadcn TooltipContent styling without glass effects.
function ToolTooltip({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="left" sideOffset={8}>
        {label}
      </TooltipContent>
    </Tooltip>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Filter: entity-type visibility toggle. Opens the filter panel rendered by
// the parent view (mirrors the Graph view's Filter UX).

function FilterButton({ active, onClick }: { active: boolean; onClick: () => void }) {
  const { t } = useTranslation()
  const entities = useGeolocationStore(s => s.entities)
  const hiddenEntityTypes = useGeolocationStore(s => s.hiddenEntityTypes)

  if (entities.length === 0) return null

  return (
    <ToolTooltip label={t('geo.tools.filter')}>
      <button type="button" onClick={onClick} className={TOOL_BUTTON_CLASSES(active, false)}>
        <span className="flex h-5 w-5 items-center justify-center">
          <Filter className="h-5 w-5" />
        </span>
        {hiddenEntityTypes.size > 0 && (
          <span
            className={cn(
              'absolute -right-1 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center',
              'rounded-full border-[1.5px] border-background bg-primary px-1 text-[9px] font-bold text-primary-foreground'
            )}
          >
            {hiddenEntityTypes.size}
          </span>
        )}
      </button>
    </ToolTooltip>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Fan-out tools button. The trigger replaces the bare timeline button; on
// open it sweeps a small semicircle to the LEFT exposing two actions:
//   1. Timeline scrubber visibility (hidden when there are no time-aware
//      entities — same condition that previously hid the bare timeline btn).
//   2. Support-graph side panel (synced selection with the map). Only
//      rendered when the parent passes a toggle handler.
// The fan auto-closes on item click. If neither action is available the
// trigger collapses to nothing — the toolbar then has one fewer button.

interface FanItem {
  key: string
  icon: React.ReactNode
  tooltip: string
  active: boolean
  onClick: () => void
}

function ToolsFanButton({
  supportViewOpen,
  onToggleSupportView,
}: {
  supportViewOpen: boolean
  onToggleSupportView?: () => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const entities = useGeolocationStore(s => s.entities)
  const timelineVisible = useGeolocationStore(s => s.timelineVisible)
  const setTimelineVisible = useGeolocationStore(s => s.setTimelineVisible)

  // The clock icon controls VISIBILITY of the bottom temporal strip
  // (filter or playback — whichever the user has selected via the in-strip
  // toggle). It appears whenever the case has any time-aware entities;
  // mode-switching itself lives in the strip.
  const hasTemporal = entities.some(
    e => e.mapRenderMode === 'animated_track' || e.mapRenderMode === 'time_anchored_pin'
  )

  const items: FanItem[] = []
  if (hasTemporal) {
    items.push({
      key: 'timeline',
      icon: <Clock className="h-5 w-5" />,
      tooltip: t('geo.tools.timeline'),
      active: timelineVisible,
      onClick: () => {
        setTimelineVisible(!timelineVisible)
        setOpen(false)
      },
    })
  }
  if (onToggleSupportView) {
    items.push({
      key: 'support',
      icon: <Network className="h-5 w-5" />,
      tooltip: t('geo.tools.supportGraph'),
      active: supportViewOpen,
      onClick: () => {
        onToggleSupportView()
        setOpen(false)
      },
    })
  }

  if (items.length === 0) return null

  // Distribute items along a semicircle opening to the left of the trigger.
  // 1 item → directly left; 2 items → top-left + bottom-left for symmetry.
  const positions = items.map((_, idx) => {
    if (items.length === 1) return { x: -56, y: 0 }
    const totalArc = 80 // degrees, kept narrow so items don't collide with
    // the toolbar buttons stacked below the trigger.
    const startAngle = 180 - totalArc / 2
    const angle = startAngle + (idx * totalArc) / (items.length - 1)
    const rad = (angle * Math.PI) / 180
    const r = 56
    return { x: Math.cos(rad) * r, y: -Math.sin(rad) * r }
  })

  const anyActive = items.some(it => it.active)

  return (
    <div className="relative">
      <ToolButton
        icon={<Layers2 className="h-5 w-5" />}
        tooltip={t('geo.tools.viewTools')}
        active={anyActive && !open}
        attached={open}
        onClick={() => setOpen(o => !o)}
      />
      <AnimatePresence>
        {open &&
          items.map((item, idx) => (
            <motion.div
              key={item.key}
              initial={{ x: 0, y: 0, opacity: 0, scale: 0.5 }}
              animate={{
                x: positions[idx].x,
                y: positions[idx].y,
                opacity: 1,
                scale: 1,
              }}
              exit={{ x: 0, y: 0, opacity: 0, scale: 0.5 }}
              transition={{
                type: 'spring',
                stiffness: 360,
                damping: 26,
                mass: 0.8,
                delay: idx * 0.04,
              }}
              className="pointer-events-auto absolute left-0 top-0"
            >
              <ToolButton
                icon={item.icon}
                tooltip={item.tooltip}
                active={item.active}
                onClick={item.onClick}
              />
            </motion.div>
          ))}
      </AnimatePresence>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Draw geofence — toggle button; while drawing, a name input slides in
// to the LEFT of the toolbar and Save / Cancel buttons appear below.

function DrawButtons() {
  const { t } = useTranslation()
  const drawingMode = useGeofenceStore(s => s.drawingMode)
  const drawingVertices = useGeofenceStore(s => s.drawingVertices)
  const drawingName = useGeofenceStore(s => s.drawingName)
  const setDrawingName = useGeofenceStore(s => s.setDrawingName)
  const startDrawing = useGeofenceStore(s => s.startDrawing)
  const cancelDrawing = useGeofenceStore(s => s.cancelDrawing)
  const saveDrawing = useGeofenceStore(s => s.saveDrawing)
  const defaultFenceHeight = useGeofenceStore(s => s.defaultFenceHeight)
  const fenceCount = useGeofenceStore(s => s.geofences.length)
  const inputRef = useRef<HTMLInputElement>(null)

  const drawing = drawingMode === 'drawing'
  const canSave = drawing && drawingVertices.length >= 3

  // Auto-focus the name input when drawing starts.
  useEffect(() => {
    if (drawing) {
      setTimeout(() => inputRef.current?.focus(), 200)
    }
  }, [drawing])

  const onSave = () => {
    if (!canSave) return
    const finalName = drawingName.trim() || `Geofence ${fenceCount + 1}`
    saveDrawing(finalName, defaultFenceHeight, false)
  }

  return (
    <>
      <ToolButton
        icon={<Hexagon className="h-5 w-5" />}
        tooltip={drawing ? t('geo.tools.drawCancel') : t('geo.tools.draw')}
        active={drawing}
        onClick={() => (drawing ? cancelDrawing() : startDrawing())}
        label={drawing ? `${drawingVertices.length}` : undefined}
      />
      <AnimatePresence initial={false}>
        {drawing && (
          // Floating panel — sits to the LEFT of the toolbar so the inline
          // toolbar column (Reset, Basemap) stays at its resting position
          // and isn't shoved down by inline Save/Cancel buttons. Holds the
          // name input, vertex counter, and Save / Cancel actions together.
          <motion.div
            key="name-input"
            initial={{ opacity: 0, x: 12, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 12, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 380, damping: 32, mass: 0.85 }}
            className={cn(
              'pointer-events-auto absolute top-12 w-[260px]',
              'flex flex-col gap-2 rounded-xl border px-3 py-2',
              'border-border/40 bg-popover/95 text-popover-foreground',
              'backdrop-blur-xl backdrop-saturate-150',
              'shadow-[0_18px_48px_-16px_hsl(var(--foreground)/0.28),inset_0_1px_0_hsl(0_0%_100%/0.10)]'
            )}
            style={{ right: 'calc(100% + 12px)' }}
          >
            <input
              ref={inputRef}
              value={drawingName}
              onChange={e => setDrawingName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && canSave) {
                  e.preventDefault()
                  onSave()
                } else if (e.key === 'Escape') {
                  e.preventDefault()
                  cancelDrawing()
                }
              }}
              placeholder={t('geo.draw.namePlaceholder')}
              className={cn(
                'w-full rounded-md border border-border/40 bg-muted/40',
                // 16px text on a phone: anything smaller makes iOS zoom the page on focus.
                'px-2 py-1.5 text-[12px] text-foreground placeholder:text-muted-foreground max-md:text-base',
                'outline-none focus:border-border/70 focus:bg-muted/60'
              )}
            />
            <div className="flex items-center justify-between text-[9px] uppercase tracking-[0.16em] text-muted-foreground/70">
              <span className="font-mono tabular-nums">{drawingVertices.length} pts</span>
              <span>{t('geo.draw.saveHint')}</span>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={onSave}
                disabled={!canSave}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-2.5 md:py-1.5',
                  'text-[11px] font-medium uppercase tracking-[0.12em] transition-colors',
                  canSave
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'bg-muted/40 text-muted-foreground/60 cursor-not-allowed'
                )}
              >
                <Save className="h-3.5 w-3.5" />
                {t('geo.tools.drawSave')}
              </button>
              <button
                type="button"
                onClick={cancelDrawing}
                className={cn(
                  'flex items-center justify-center gap-1.5 rounded-md border px-2 py-2.5 md:py-1.5',
                  'border-border/40 bg-muted/30 text-foreground/80',
                  'text-[11px] font-medium uppercase tracking-[0.12em] transition-colors',
                  'hover:border-border/60 hover:bg-muted/50 hover:text-foreground'
                )}
              >
                <X className="h-3.5 w-3.5" />
                {t('geo.tools.drawCancel')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Reset viewport.

function ResetButton({ mapRef }: { mapRef: React.RefObject<MapLibreMap | null> }) {
  const { t } = useTranslation()
  const entities = useGeolocationStore(s => s.entities)
  const reset = () => {
    const map = mapRef.current
    if (!map) return
    if (entities.length > 0) {
      let minLng = Infinity
      let maxLng = -Infinity
      let minLat = Infinity
      let maxLat = -Infinity
      for (const e of entities) {
        if (e.longitude < minLng) minLng = e.longitude
        if (e.longitude > maxLng) maxLng = e.longitude
        if (e.latitude < minLat) minLat = e.latitude
        if (e.latitude > maxLat) maxLat = e.latitude
      }
      if (Number.isFinite(minLng)) {
        map.fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat],
          ],
          { padding: 80, maxZoom: 14, duration: 700 }
        )
        return
      }
    }
    map.flyTo({ center: HOME_VIEW.center, zoom: HOME_VIEW.zoom, duration: 700 })
  }
  return (
    <ToolButton
      icon={<Home className="h-5 w-5" />}
      tooltip={t('geo.tools.reset')}
      onClick={reset}
    />
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Basemap dropdown.

function BasemapButton() {
  const { t } = useTranslation()
  const baseLayer = useGeolocationStore(s => s.baseLayer)
  const setBaseLayer = useGeolocationStore(s => s.setBaseLayer)
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <ToolTooltip label={t('geo.tools.basemap')}>
        <PopoverTrigger asChild>
          <button type="button" className={TOOL_BUTTON_CLASSES(false, open)}>
            <span className="flex h-5 w-5 items-center justify-center">
              <MapIcon className="h-5 w-5" />
            </span>
          </button>
        </PopoverTrigger>
      </ToolTooltip>
      <PopoverContent
        side="left"
        align="start"
        sideOffset={10}
        className={cn(
          'w-[180px] overflow-hidden rounded-2xl border-border/40 p-1',
          'bg-popover/90 backdrop-blur-xl backdrop-saturate-150 text-popover-foreground',
          'shadow-[0_24px_64px_-16px_hsl(var(--foreground)/0.32),inset_0_1px_0_hsl(0_0%_100%/0.08)]'
        )}
      >
        {SUPPORTED_LAYERS.map(layer => (
          <button
            key={layer.id}
            type="button"
            onClick={() => {
              setBaseLayer(layer.id)
              setOpen(false)
            }}
            className={cn(
              'flex w-full items-center gap-2 rounded-md px-2.5 py-2.5 text-left text-[12px] md:py-1.5',
              'transition-colors hover:bg-accent/60',
              baseLayer === layer.id ? 'text-primary font-medium' : 'text-foreground'
            )}
          >
            {layer.label}
            {baseLayer === layer.id && (
              <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary" />
            )}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
}
